import fr from 'facet-rest';
import RichTextEditor from 'Components/UI/RichTextEditor';
import Autocomplete from 'Components/UI/Autocomplete';
import GroupedSelectControl from 'Components/UI/GroupedSelectControl';
import ParaplannerPicklistOptions from 'Resources/ParaplannerPicklistOptions';
import TextField from 'Components/UI/TextField';
import PriorityButtonGroup from 'Components/UI/Overrides/PriorityButtonGroup';
import RichTextDisplay from 'Components/UI/Overrides/RichTextDisplay';

const statusColors = {
  todo: 'todoBlue',
  'in-progress': 'progressYellow',
  pending: 'pendingPink',
  review: 'reviewPurple',
  completed: 'completeGreen',
  'not-applicable': 'inherit',
};


const hiddenFieldNames = [
  'description',
  'showOnBlueprint',
  'timeToComplete',
  'assignedTo',
  'hidden',
  'createdAt',
  'ownerType',
  'completedAt',
  'householdId',
  'priority',
  'title',
];
export const SimpleActionItemTableOverride = fr.metaOverride()
  .overrideAll({
    readonly: true,
    _hideLabel: true,
    _customProps: {
      noWrap: true,
      hasTooltip: true,
    },
  })
  .nameOverride(name => hiddenFieldNames.includes(name), { hidden: true })
  .nameOverride('clientName', {
    ordinal: 0,
    _customProps: {
      color: 'primary',
      noWrap: true,
      hasTooltip: true,
    },
  })
  .nameOverride('name', { ordinal: 1 })
  .nameOverride('ownerName', { ordinal: 2 })
  .nameOverride('status', {
    ordinal: 3,
    _customProps: {
      color: ({ resource }) => {
        let c;
        if (resource.attr('status') in statusColors) {
          c = statusColors[resource.attr('status')];
        }
        return c;
      },
      variant: 'filled',
      SelectProps: {
        disableUnderline: true,
      },
    },
    readonly: false,
  })
  .nameOverride('dueDate', { readonly: false, ordinal: 5 })
  .nameOverride('leadPlannerName', { ordinal: 6 })
  .nameOverride('createdByName', { ordinal: 7 })
  .nameOverride('actionItemType', {
    ordinal: 10,
  });

const editHiddenFieldNames = [
  'hidden',
  'ownerName',
  'clientName',
  'completedAt',
  'leadPlannerName',
  'householdId',
  'ownerType',
  'actionItemType',
  'title',
];

export const SimpleActionItemEditOverride = fr.metaOverride()
  .nameOverride(name => editHiddenFieldNames.includes(name), {
    hidden: true,
  })
  .nameOverride('description', {
    hidden: false,
    _component: () => RichTextEditor,
    _customProps: {
      size: 'md',
    },
  })
  .nameOverride('name', {
    ordinal: 1,
    _customProps: {
      inputProps: {
        id: 'simple-action-item-field-name',
      },
    },
  })
  .nameOverride('dueDate', { ordinal: 2 })
  .nameOverride('priority', { ordinal: 3 })
  .nameOverride('status', { ordinal: 4 })
  .nameOverride('timeToComplete', { ordinal: 5 })
  .nameOverride('assignedTo', {
    ordinal: 6,
    _component: () => Autocomplete,
    _customProps: {
      ignoreGroups: true,
    },
  })
  .nameOverride('description', { ordinal: 8 })
  .nameOverride('showOnBlueprint', { ordinal: 9 })
  .nameOverride('createdAt', { ordinal: 10 })
  .nameOverride('createdByName', { ordinal: 11 })
  .showRequiredFields();

const draftHiddenFieldNames = [
  'hidden',
  'ownerName',
  'clientName',
  'completedAt',
  'leadPlannerName',
  'householdId',
  'ownerType',
  'publishedDate',
  'createdByName',
  'createdAt',
  'title',
];

export const SimpleActionItemDraftOverride = fr.metaOverride()
  .nameOverride(name => draftHiddenFieldNames.includes(name), {
    hidden: true,
  })
  .nameOverride('description', {
    hidden: false,
    _component: () => RichTextEditor,
    _customProps: {
      size: 'sm',
    },
  })
  .nameOverride('name', {
    ordinal: 1,
    _customProps: {
      inputProps: {
        id: 'simple-action-item-field-name',
      },
    },
  })
  .nameOverride('dueDate', { ordinal: 2 })
  .nameOverride('priority', { ordinal: 3 })
  .nameOverride('status', { ordinal: 4 })
  .nameOverride('timeToComplete', { ordinal: 5 })
  .nameOverride(['assignedTo', 'pillar'], {
    ordinal: 6,
    _component: () => Autocomplete,
    _customProps: {
      ignoreGroups: true,
    },
  })
  .nameOverride('description', { ordinal: 8 })
  .nameOverride('showOnBlueprint', { ordinal: 9 })
  .showRequiredFields();

const editParaplannerHiddenFieldNames = [
  'hidden',
  'ownerName',
  'clientName',
  'completedAt',
  'leadPlannerName',
  'householdId',
  'ownerType',
  'priority',
  'status',
  'timeToComplete',
  'showOnBlueprint',
  'title',
];

export const SimpleParaplannerActionItemEditOverride = fr.metaOverride()
  .nameOverride(name => editParaplannerHiddenFieldNames.includes(name), { hidden: true })
  .nameOverride('description', {
    hidden: false,
    _component: () => RichTextEditor,
    _customProps: {
      size: 'md',
    },
  })
  .nameOverride('name', {
    ordinal: 1,
    _component: () => GroupedSelectControl,
    _showAllOptions: true,
    _customProps: {
      options: ParaplannerPicklistOptions,
      inputProps: {
        id: 'simple-action-item-field-name',
      },
    },
  })
  .nameOverride('dueDate', { ordinal: 2 })
  .nameOverride('timeToComplete', { ordinal: 3 })
  .nameOverride('assignedTo', { ordinal: 4, _component: () => Autocomplete })
  .nameOverride('description', { ordinal: 5 })
  .nameOverride('createdAt', { ordinal: 6 })
  .nameOverride('createdByName', { ordinal: 7 })
  .showRequiredFields();

const clientHiddenFieldNames = ['meetingId', 'assignedTo', 'calendarEventId', 'notApplicableDescription', 'status', 'showOnBlueprint', 'ownerId', 'householdId', 'clientId', 'timeToComplete', 'hidden', 'title'];

export const ClientSimpleActionItemOverride = fr.metaOverride()
  .override({ readonly: true }, { hidden: true })
  .nameOverride(name => clientHiddenFieldNames.includes(name), { hidden: true })
  .nameOverride('name', {
    prompt: 'Title',
    label: 'Title',
    readonly: true,
    _component: () => TextField,
    _customProps: { multiline: true },
    display: true,
    hidden: false,
  })
  .nameOverride('priority', {
    readonly: true, _component: () => PriorityButtonGroup, display: true, hidden: false,
  })
  .nameOverride('dueDate', { readonly: true, display: true, hidden: false })
  .nameOverride(
    'description',
    {
      display: true,
      readonly: false, // although it's technically readonly, setting readonly to true causes the component override to be ignored
      hidden: false,
      _component: () => RichTextDisplay,
      ordinal: 101,
    },
  );
